import { AuthApolloProvider, AuthApolloProviderProps } from "@hydrologiq/common/providers/AuthApolloProvider"
import { Outlet } from "react-router-dom"
import Informative from "@hydrologiq/shared/Informative"
import { DashboardUser } from "@hydrologiq/common/providers/FirebaseAuth"
import { RestApiProvider } from "@hydrologiq/shared/providers/RestApiProvider"
import { DualGraphQLProvider } from "@hydrologiq/shared/providers/DualGraphQLProvider"

interface AuthProps {
  user?: DashboardUser
  token?: string
  authType?: "firebase" | "better-auth"
  graphqlUri?: string // Made optional for backward compatibility
  primaryGraphqlUri?: string // Hub's modern GraphQL
  legacyGraphqlUri?: string // Section's legacy GraphQL
  restEndpoint: string
  children?: React.ReactNode
  cacheConfig?: AuthApolloProviderProps["cacheConfig"]
}

function Auth(props: AuthProps) {
  const {
    user,
    token,
    authType,
    graphqlUri,
    primaryGraphqlUri,
    legacyGraphqlUri,
    restEndpoint,
    children,
    cacheConfig,
  } = props

  // Determine authentication method and requirements
  const hasAuth = user || token
  const hasBetterAuthToken = token || localStorage.getItem("bearer_token")

  if (!hasAuth && !hasBetterAuthToken) {
    return <Informative heading="Please contact support" text="Failed to retrieve user profile." />
  }

  // Determine which endpoints to use
  const finalLegacyEndpoint = legacyGraphqlUri || graphqlUri
  const finalPrimaryEndpoint = primaryGraphqlUri

  if (!finalLegacyEndpoint) {
    return <Informative heading="Configuration Error" text="No GraphQL endpoint configured." />
  }

  // Use DualGraphQLProvider if we have dual endpoints or need better-auth support
  const shouldUseDualProvider = finalPrimaryEndpoint || token || authType === "better-auth"

  if (shouldUseDualProvider) {
    return (
      <DualGraphQLProvider
        primaryEndpoint={finalPrimaryEndpoint}
        legacyEndpoint={finalLegacyEndpoint}
        user={user}
        token={token}
        authType={authType}
        cacheConfig={cacheConfig}
      >
        <RestApiProvider user={user} restEndpoint={restEndpoint} token={token} authType={authType}>
          {children ? children : <Outlet />}
        </RestApiProvider>
      </DualGraphQLProvider>
    )
  }

  // Fallback to legacy AuthApolloProvider for backward compatibility
  return user ? (
    <AuthApolloProvider uri={finalLegacyEndpoint} user={user} cacheConfig={cacheConfig}>
      <RestApiProvider user={user} restEndpoint={restEndpoint} token={token} authType={authType}>
        {children ? children : <Outlet />}
      </RestApiProvider>
    </AuthApolloProvider>
  ) : (
    <Informative heading="Please contact support" text="Failed to retrieve user profile." />
  )
}

export default Auth
